import { memo } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Clock, Pencil, Trash2 } from "lucide-react";
import { formatDuration, formatTimeRange } from "@/lib/time-utils";
import type { TimeEntry } from "@/types/time";

interface TimeEntryRowProps {
  entry: TimeEntry;
  onEdit?: (entry: TimeEntry) => void;
  onDelete?: (id: string) => void;
}

export const TimeEntryRow = memo(function TimeEntryRow({ entry, onEdit, onDelete }: TimeEntryRowProps) {
  const showActions = !!onEdit || !!onDelete;

  return (
    <div className="flex items-center justify-between rounded-md border px-3 py-2">
      <div className="flex items-center gap-3 min-w-0">
        <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0">
          <p className="text-sm truncate">
            <span className="font-medium">{formatDuration(entry.durationSeconds)}</span>
            <span className="ml-2 text-xs text-muted-foreground">{formatTimeRange(entry.start, entry.end)}</span>
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {entry.project?.name ?? "No project"}
            {entry.task && ` · ${entry.task.name}`}
            {entry.description && ` — ${entry.description}`}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {showActions && (
          <div className="flex items-center gap-1">
            {onEdit && (
              <Button variant="ghost" size="sm" aria-label="Edit entry" onClick={() => onEdit(entry)}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
            {onDelete && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" aria-label="Delete entry">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete entry?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete this time entry.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => onDelete(entry.id)}>
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        )}
      </div>
    </div>
  );
});
